import React, { useState } from 'react';
import {
  Table, Card, Button, Space, Typography, Form, Input, Select, Radio,
  Drawer, Tag, message, Upload, Popconfirm, Divider, Modal, Alert
} from 'antd';
import { PlusOutlined, EditOutlined, InboxOutlined, ExportOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// ────────────────────────────────────────────
//  Mock 数据
// ────────────────────────────────────────────

const PRODUCT_MOCK = [
  { value: 'PROD_Q1', label: '智能摄像机Q1', totalDevices: 362 },
  { value: 'PROD_P1', label: '云台机P1', totalDevices: 280 },
  { value: 'PROD_F1', label: '烟雾报警器F1', totalDevices: 150 },
  { value: 'PROD_D1', label: '智能门铃Pro', totalDevices: 90 },
];

const ORDER_MOCK = {
  'PROD_Q1': [
    { orderId: 'WO-2026-001', orderName: '摄像机Q1-批次001', deviceCount: 200 },
    { orderId: 'WO-2026-002', orderName: '摄像机Q1-批次002', deviceCount: 162 },
  ],
  'PROD_P1': [
    { orderId: 'WO-2026-003', orderName: '云台机P1-批次003', deviceCount: 200 },
    { orderId: 'WO-2026-004', orderName: '云台机P1-批次004', deviceCount: 80 },
  ],
  'PROD_F1': [
    { orderId: 'WO-2026-005', orderName: '烟雾报警器F1-批次001', deviceCount: 100 },
    { orderId: 'WO-2026-006', orderName: '烟雾报警器F1-批次002', deviceCount: 50 },
  ],
};

const STRATEGY_MAP = {
  product: { text: '按产品', color: 'blue' },
  order: { text: '按生产', color: 'green' },
  custom: { text: '导入设备', color: 'orange' },
};

const MOCK_LICENSES = [
  { key: '1', license: 'test-license-001', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderName: '摄像机Q1-批次001', orderId: 'WO-2026-001', activated: true, bound: true },
  { key: '2', license: 'test-license-002', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderName: '摄像机Q1-批次001', orderId: 'WO-2026-001', activated: true, bound: false },
  { key: '3', license: 'test-license-003', productName: '云台机P1', productId: 'PROD_P1', orderName: '云台机P1-批次003', orderId: 'WO-2026-003', activated: false, bound: false },
  { key: '4', license: 'test-license-004', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderName: '摄像机Q1-批次002', orderId: 'WO-2026-002', activated: true, bound: true },
  { key: '5', license: 'test-license-005', productName: '烟雾报警器F1', productId: 'PROD_F1', orderName: '烟雾报警器F1-批次001', orderId: 'WO-2026-005', activated: true, bound: true },
  { key: '6', license: 'test-license-006', productName: '智能门铃Pro', productId: 'PROD_D1', orderName: '—', orderId: '—', activated: true, bound: false },
  { key: '7', license: 'test-license-007', productName: '云台机P1', productId: 'PROD_P1', orderName: '云台机P1-批次003', orderId: 'WO-2026-003', activated: false, bound: false },
  { key: '8', license: 'test-license-008', productName: '智能摄像机Q1', productId: 'PROD_Q1', orderName: '摄像机Q1-批次001', orderId: 'WO-2026-001', activated: true, bound: true },
];

const MOCK_GROUPS = [
  { key: 'g1', name: 'Q1产品组', description: '智能摄像机Q1全量设备', strategy: 'product', products: [{ productId: 'PROD_Q1', productName: '智能摄像机Q1', deviceCount: 362 }], deviceCount: 362, createTime: '2026-07-14 10:00', updateTime: '2026-07-15 09:30' },
  { key: 'g2', name: '工单批次组', description: '多产品生产批次设备', strategy: 'order', orders: [{ productId: 'PROD_Q1', productName: '智能摄像机Q1', orderId: 'WO-2026-001', orderName: '摄像机Q1-批次001', deviceCount: 200 }, { productId: 'PROD_P1', productName: '云台机P1', orderId: 'WO-2026-003', orderName: '云台机P1-批次003', deviceCount: 200 }], deviceCount: 400, createTime: '2026-07-13 15:30', updateTime: '2026-07-14 11:00' },
  { key: 'g3', name: '华南外协', description: '华南外协厂返厂设备', strategy: 'custom', deviceCount: 120, createTime: '2026-07-12 09:15', updateTime: '2026-07-13 16:00' },
];

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

const DeviceGroups = () => {
  const [groups, setGroups] = useState(MOCK_GROUPS);
  const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
  const [nameModalVisible, setNameModalVisible] = useState(false);
  const [nameEditGroup, setNameEditGroup] = useState(null);
  const [form] = Form.useForm();
  const [nameForm] = Form.useForm();

  // 创建
  const [strategy, setStrategy] = useState('product');
  const [createProductIds, setCreateProductIds] = useState([]);
  const [createOrders, setCreateOrders] = useState([]);

  // 查看设备（含管理）
  const [viewVisible, setViewVisible] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [viewLicense, setViewLicense] = useState('');
  const [viewProdType, setViewProdType] = useState('name');
  const [viewProdValue, setViewProdValue] = useState('');
  const [viewOrderType, setViewOrderType] = useState('name');
  const [viewOrderValue, setViewOrderValue] = useState('');
  const [viewSaving, setViewSaving] = useState(false);

  // 编辑态副本
  const [editProducts, setEditProducts] = useState([]);
  const [editOrders, setEditOrders] = useState([]);

  // 添加工单弹窗
  const [addOrderModalVisible, setAddOrderModalVisible] = useState(false);
  const [addOrderProduct, setAddOrderProduct] = useState(null);
  const [addOrderIds, setAddOrderIds] = useState([]);

  // 导入设备上传状态
  const [customUploading, setCustomUploading] = useState(false);
  const [customUploadPercent, setCustomUploadPercent] = useState(0);

  const now = () => new Date().toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const usedProductIds = groups.filter((g) => g.strategy === 'product').flatMap((g) => (g.products || []).map((p) => p.productId));

  // ── 创建 ──
  const handleCreate = () => { setStrategy('product'); setCreateProductIds([]); setCreateOrders([]); form.resetFields(); setCreateDrawerVisible(true); };
  const handleCreateSave = () => {
    form.validateFields().then((values) => {
      let deviceCount = 0, products = [], orders = [];
      if (strategy === 'product') {
        products = createProductIds.map((pid) => { const p = PRODUCT_MOCK.find((x) => x.value === pid); const dc = p?.totalDevices || 0; deviceCount += dc; return { productId: pid, productName: p?.label || '', deviceCount: dc }; });
      } else if (strategy === 'order') {
        orders = createOrders; deviceCount = createOrders.reduce((s, o) => s + o.deviceCount, 0);
      }
      const t = now();
      setGroups((prev) => [{ key: `g${Date.now()}`, name: values.name, description: values.description || '', strategy, products, orders, deviceCount, createTime: t, updateTime: t }, ...prev]);
      message.success('分组创建成功'); setCreateDrawerVisible(false);
    }).catch(() => {});
  };

  // ── 名称编辑 ──
  const openNameModal = (r) => { setNameEditGroup(r); nameForm.setFieldsValue({ name: r.name, description: r.description }); setNameModalVisible(true); };
  const handleNameSave = () => { nameForm.validateFields().then((v) => { setGroups((prev) => prev.map((g) => g.key === nameEditGroup.key ? { ...g, name: v.name, description: v.description || '', updateTime: now() } : g)); message.success('已更新'); setNameModalVisible(false); }).catch(() => {}); };

  // ── 快速更新设备 ──
  const handleQuickRefresh = (record) => {
    setViewSaving(true);
    const hide = message.loading('设备更新中...', 0);
    setTimeout(() => {
      const products = (record.products || []).map((p) => {
        const prod = PRODUCT_MOCK.find((x) => x.value === p.productId);
        return { productId: p.productId, productName: prod?.label || '', deviceCount: prod?.totalDevices || 0 };
      });
      const deviceCount = products.reduce((s, p) => s + p.deviceCount, 0);
      setGroups((prev) => prev.map((g) => g.key === record.key ? { ...g, products, deviceCount, updateTime: now() } : g));
      hide(); message.success('设备更新成功'); setViewSaving(false);
    }, 1500);
  };
  const handleDelete = (key) => { setGroups((prev) => prev.filter((g) => g.key !== key)); message.success('分组已删除'); };

  // ── 查看设备（打开时同步编辑态副本） ──
  const openView = (r) => {
    setViewTarget(r); setViewLicense(''); setViewProdType('name'); setViewProdValue(''); setViewOrderType('name'); setViewOrderValue('');
    setEditProducts([...(r.products || [])]); setEditOrders([...(r.orders || [])]);
    setViewVisible(true);
  };

  // ── 保存并更新设备 ──
  const handleViewSave = () => {
    setViewSaving(true);
    const hide = message.loading('设备更新中...', 0);
    setTimeout(() => {
      let deviceCount = 0;
      if (viewTarget.strategy === 'product') {
        deviceCount = editProducts.reduce((s, p) => { const prod = PRODUCT_MOCK.find((x) => x.value === p.productId); return s + (prod?.totalDevices || 0); }, 0);
        const prods = editProducts.map((p) => { const prod = PRODUCT_MOCK.find((x) => x.value === p.productId); return { productId: p.productId, productName: prod?.label || '', deviceCount: prod?.totalDevices || 0 }; });
        setGroups((prev) => prev.map((g) => g.key === viewTarget.key ? { ...g, products: prods, deviceCount, updateTime: now() } : g));
        setViewTarget((prev) => ({ ...prev, products: prods, deviceCount, updateTime: now() }));
      } else if (viewTarget.strategy === 'order') {
        deviceCount = editOrders.reduce((s, o) => s + o.deviceCount, 0);
        setGroups((prev) => prev.map((g) => g.key === viewTarget.key ? { ...g, orders: editOrders, deviceCount, updateTime: now() } : g));
        setViewTarget((prev) => ({ ...prev, orders: editOrders, deviceCount, updateTime: now() }));
      } else {
        deviceCount = viewTarget.deviceCount + 50;
        setGroups((prev) => prev.map((g) => g.key === viewTarget.key ? { ...g, deviceCount, updateTime: now() } : g));
        setViewTarget((prev) => ({ ...prev, deviceCount, updateTime: now() }));
      }
      hide(); message.success('设备更新成功'); setViewSaving(false);
    }, 1500);
  };

  // ── 筛选 License ──
  const viewLicensesFiltered = (() => {
    let data = MOCK_LICENSES;
    if (viewLicense) data = data.filter((r) => r.license.includes(viewLicense));
    if (viewProdValue) {
      if (viewProdType === 'name') data = data.filter((r) => r.productName.includes(viewProdValue));
      else data = data.filter((r) => r.productId.includes(viewProdValue));
    }
    if (viewOrderValue) {
      if (viewOrderType === 'name') data = data.filter((r) => r.orderName.includes(viewOrderValue));
      else data = data.filter((r) => r.orderId.includes(viewOrderValue));
    }
    return data;
  })();

  // ── 添加产品 ──
  const addProductsToEdit = () => {
    if (addOrderIds.length === 0) return;
    const news = addOrderIds.map((pid) => ({ productId: pid, productName: PRODUCT_MOCK.find((x) => x.value === pid)?.label || '' }));
    const existingIds = editProducts.map((p) => p.productId);
    setEditProducts((prev) => [...prev, ...news.filter((n) => !existingIds.includes(n.productId))]);
    setAddOrderIds([]); setAddOrderModalVisible(false);
  };

  // ── 添加工单 ──
  const addOrdersToEdit = () => {
    if (!addOrderProduct || addOrderIds.length === 0) return;
    const prod = PRODUCT_MOCK.find((p) => p.value === addOrderProduct);
    const news = addOrderIds.map((oid) => { const o = (ORDER_MOCK[addOrderProduct] || []).find((x) => x.orderId === oid); return { productId: addOrderProduct, productName: prod?.label || '', orderId: oid, orderName: o?.orderName || '', deviceCount: o?.deviceCount || 0 }; });
    const existingIds = editOrders.map((o) => o.orderId);
    setEditOrders((prev) => [...prev, ...news.filter((n) => !existingIds.includes(n.orderId))]);
    setAddOrderProduct(null); setAddOrderIds([]); setAddOrderModalVisible(false);
  };

  // 通用添加工单/产品弹窗
  const openAddModal = () => { setAddOrderProduct(null); setAddOrderIds([]); setAddOrderModalVisible(true); };

  // 列表筛选
  const [listLicense, setListLicense] = useState('');
  const [listProdType, setListProdType] = useState('name');
  const [listProdValue, setListProdValue] = useState('');
  const [listName, setListName] = useState('');
  const [listOrderType, setListOrderType] = useState('name');
  const [listOrderValue, setListOrderValue] = useState('');

  const filteredGroups = (() => {
    let data = groups;
    if (listLicense) data = data.filter((g) => {
      if (g.strategy === 'product') return (g.products || []).some((p) => p.productId?.includes(listLicense));
      if (g.strategy === 'order') return (g.orders || []).some((o) => o.orderId?.includes(listLicense));
      return false;
    });
    if (listProdValue) {
      if (listProdType === 'name') data = data.filter((g) => (g.products || []).some((p) => p.productName?.includes(listProdValue)) || (g.orders || []).some((o) => o.productName?.includes(listProdValue)));
      else data = data.filter((g) => (g.products || []).some((p) => p.productId?.includes(listProdValue)) || (g.orders || []).some((o) => o.productId?.includes(listProdValue)));
    }
    if (listName) data = data.filter((g) => g.name.includes(listName));
    if (listOrderValue) {
      if (listOrderType === 'name') data = data.filter((g) => (g.orders || []).some((o) => o.orderName?.includes(listOrderValue)));
      else data = data.filter((g) => (g.orders || []).some((o) => o.orderId?.includes(listOrderValue)));
    }
    return data;
  })();
  const columns = [
    { title: '分组名称', dataIndex: 'name', width: 180, render: (v, r) => (<Space size={4}><Text strong>{v}</Text><Button type="link" size="small" icon={<EditOutlined />} style={{ padding: 0, color: '#999' }} onClick={() => openNameModal(r)} /></Space>) },
    { title: '分组描述', dataIndex: 'description', width: 180, ellipsis: true, render: (v) => v ? <Text type="secondary" ellipsis>{v}</Text> : <Text style={{ color: '#ccc' }}>—</Text> },
    { title: '分组方式', dataIndex: 'strategy', width: 100, render: (v) => <Tag color={STRATEGY_MAP[v]?.color} style={{ border: 'none' }}>{STRATEGY_MAP[v]?.text}</Tag> },
    { title: '关联信息', key: 'relate', width: 180, render: (_, r) => { if (r.strategy === 'product') return <Text>{(r.products || []).length} 个产品</Text>; if (r.strategy === 'order') return <Text>{(r.orders || []).length} 个工单</Text>; return <Text type="secondary">—</Text>; } },
    { title: '设备数量', dataIndex: 'deviceCount', width: 90, sorter: (a, b) => a.deviceCount - b.deviceCount, showSorterTooltip: { title: '点击排序' } },
    { title: '创建时间', dataIndex: 'createTime', width: 150 },
    { title: '更新时间', dataIndex: 'updateTime', width: 150 },
    { title: '操作', key: 'action', width: 200, fixed: 'right', render: (_, r) => (
      <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
        {r.strategy === 'product' && <Button type="link" size="small" loading={viewSaving && viewTarget?.key === r.key} onClick={() => handleQuickRefresh(r)}>更新设备</Button>}
        <Button type="link" size="small" onClick={() => openView(r)}>管理设备</Button>
        <Popconfirm title="确认删除该分组？" onConfirm={() => handleDelete(r.key)} okText="确认" cancelText="取消"><Button type="link" size="small" danger>删除</Button></Popconfirm>
      </Space>
    ) },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>设备分组</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>创建分组</Button>
      </div>
      <Alert type="info" showIcon closable message="为了便于您更好的管理设备，建议您使用分组的方式来批量管理设备。以产品作为分组条件的设备数会根据条件每小时更新一次" style={{ marginBottom: 16, borderRadius: 8 }} />

      {/* 列表筛选 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '12px 0' }}>
        <Form layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0', padding: '0 24px' }}>
          <Form.Item style={{ marginRight: 8 }}>
            <Input placeholder="License" value={listLicense} onChange={(e) => setListLicense(e.target.value)} allowClear style={{ width: 160 }} />
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Select value={listProdType} onChange={(v) => { setListProdType(v); setListProdValue(''); }} style={{ width: 100 }}>
                <Select.Option value="name">产品名称</Select.Option>
                <Select.Option value="id">产品ID</Select.Option>
              </Select>
              {listProdType === 'name' ? (
                <Select value={listProdValue} onChange={setListProdValue} placeholder="请选择产品" allowClear showSearch optionFilterProp="label" style={{ width: 140 }} options={PRODUCT_MOCK.map((p) => ({ value: p.label, label: p.label }))} />
              ) : (
                <Input value={listProdValue} onChange={(e) => setListProdValue(e.target.value)} placeholder="产品ID" allowClear style={{ width: 140 }} />
              )}
            </Input.Group>
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Input placeholder="分组名称" value={listName} onChange={(e) => setListName(e.target.value)} allowClear style={{ width: 140 }} />
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Select value={listOrderType} onChange={(v) => { setListOrderType(v); setListOrderValue(''); }} style={{ width: 100 }}>
                <Select.Option value="name">工单名称</Select.Option>
                <Select.Option value="id">工单ID</Select.Option>
              </Select>
              {listOrderType === 'name' ? (
                <Select value={listOrderValue} onChange={setListOrderValue} placeholder="请选择工单" allowClear showSearch optionFilterProp="label" style={{ width: 140 }} options={Object.values(ORDER_MOCK).flat().map((o) => ({ value: o.orderName, label: o.orderName }))} />
              ) : (
                <Input value={listOrderValue} onChange={(e) => setListOrderValue(e.target.value)} placeholder="工单ID" allowClear style={{ width: 140 }} />
              )}
            </Input.Group>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={() => {}}>查询</Button>
              <Button onClick={() => { setListLicense(''); setListProdType('name'); setListProdValue(''); setListName(''); setListOrderType('name'); setListOrderValue(''); }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Table columns={columns} dataSource={filteredGroups} rowKey="key" scroll={{ x: 1280 }} pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 个分组` }} locale={{ emptyText: '暂无分组，点击右上角「创建分组」开始' }} />
      </Card>

      {/* ── 创建分组 ── */}
      <Drawer title="创建分组" open={createDrawerVisible} onClose={() => setCreateDrawerVisible(false)} width={560} destroyOnClose
        footer={<div style={{ textAlign: 'right' }}><Space><Button onClick={() => setCreateDrawerVisible(false)}>取消</Button><Button type="primary" onClick={handleCreateSave}>确认创建</Button></Space></div>}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="name" label="分组名称" rules={[{ required: true, message: '请输入' }]}><Input placeholder="请输入分组名称" maxLength={30} /></Form.Item>
          <Form.Item name="description" label="分组描述"><Input.TextArea placeholder="请输入（可选）" maxLength={200} rows={3} showCount /></Form.Item>
          <Form.Item label="分组方式">
            <Radio.Group value={strategy} onChange={(e) => { setStrategy(e.target.value); setCreateProductIds([]); setCreateOrders([]); }}>
              <Radio value="product">按产品</Radio>
              <Radio value="order">按生产</Radio>
              <Radio value="custom">导入设备</Radio>
            </Radio.Group>
          </Form.Item>
          <Divider style={{ margin: '12px 0' }} />
          {strategy === 'product' && (
            <Form.Item label="选择产品（可多选）" extra="已被其他分组添加的产品不可再选">
              <Select mode="multiple" value={createProductIds} onChange={setCreateProductIds} placeholder="请选择产品"
                options={PRODUCT_MOCK.map((p) => ({ value: p.value, label: `${p.label}（${p.totalDevices}台）`, disabled: usedProductIds.includes(p.value) }))} />
            </Form.Item>
          )}
          {strategy === 'order' && (<>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}><Text strong>已选工单（{createOrders.length}）</Text><Button size="small" type="link" icon={<PlusOutlined />} onClick={openAddModal}>添加工单</Button></div>
            {createOrders.length === 0 ? <div style={{ textAlign: 'center', padding: 24, color: '#ccc' }}>暂无工单</div> : (
              <Table dataSource={createOrders.map((o, i) => ({ ...o, key: i }))} rowKey="key" size="small" pagination={false}
                columns={[{ title: '工单名称', dataIndex: 'orderName' }, { title: '产品', dataIndex: 'productName' }, { title: '设备数', dataIndex: 'deviceCount', width: 80 }, { title: '操作', width: 60, render: (_, r) => <Button type="link" size="small" danger onClick={() => setCreateOrders((prev) => prev.filter((o) => o.orderId !== r.orderId))}>移除</Button> }]} />
            )}
          </>)}
          {strategy === 'custom' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Upload showUploadList={false} accept=".xlsx,.xls,.csv" beforeUpload={() => { message.success('上传完成，设备已添加（Mock）'); return false; }}>
                  <Button icon={<InboxOutlined />}>添加设备</Button>
                </Upload>
                <Button type="link" size="small" onClick={() => message.info('下载模板（Mock）')}>下载模板</Button>
              </div>
              <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>单次最大支持 20,000 台设备</Text>
            </div>
          )}
        </Form>
      </Drawer>

      {/* ── 查看设备（含管理） ── */}
      <Drawer title={`管理设备 — ${viewTarget?.name || ''}`} open={viewVisible} onClose={() => setViewVisible(false)} width="70%" destroyOnClose>
        {/* 概览 */}
        {viewTarget && (
          <Card size="small" style={{ marginBottom: 16, background: '#fafafa', borderRadius: 8 }}>
            <div style={{ display: 'flex', gap: 32, marginBottom: (viewTarget.strategy !== 'custom' && (viewTarget.strategy === 'product' ? editProducts.length : editOrders.length) > 0) ? 12 : 0 }}>
              <div><Text type="secondary">设备总数</Text><br /><Text strong style={{ fontSize: 18 }}>{viewTarget.deviceCount}</Text></div>
              <div><Text type="secondary">更新时间</Text><br /><Text>{viewTarget.updateTime}</Text></div>
              <div><Text type="secondary">分组方式</Text><br /><Tag color={STRATEGY_MAP[viewTarget.strategy]?.color} style={{ border: 'none', marginTop: 2 }}>{viewTarget.strategy === 'product' ? '按产品分组' : viewTarget.strategy === 'order' ? '按生产分组' : '导入设备'}</Tag></div>
              {viewTarget.strategy === 'product' && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Button size="small" loading={viewSaving} onClick={handleViewSave}>更新设备</Button>
                </div>
              )}
            </div>

            {/* 按产品：产品列表 + 添加产品 */}
            {viewTarget.strategy === 'product' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editProducts.length > 0 ? 8 : 0 }}>
                  <Text strong>产品列表</Text>
                  <Button size="small" type="link" icon={<PlusOutlined />} onClick={openAddModal}>添加产品</Button>
                </div>
                {editProducts.length > 0 && (
                  <Table size="small" pagination={false} dataSource={editProducts.map((p, i) => ({ ...p, key: i }))} rowKey="key"
                    columns={[{ title: '产品信息', key: 'prod', render: (_, r) => (<div><div>{r.productName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>) }, { title: '设备数', dataIndex: 'deviceCount', width: 80, render: (_, r) => { const prod = PRODUCT_MOCK.find((x) => x.value === r.productId); return prod?.totalDevices || 0; } }, { title: '操作', width: 60, render: (_, r) => <Button type="link" size="small" danger onClick={() => setEditProducts((prev) => prev.filter((p) => p.productId !== r.productId))}>移除</Button> }]} />
                )}
              </>
            )}

            {/* 按生产：工单列表 + 添加工单 */}
            {viewTarget.strategy === 'order' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editOrders.length > 0 ? 8 : 0 }}>
                  <Text strong>生产工单列表</Text>
                  <Button size="small" type="link" icon={<PlusOutlined />} onClick={openAddModal}>添加工单</Button>
                </div>
                {editOrders.length > 0 && (
                  <Table size="small" pagination={false} dataSource={editOrders.map((o, i) => ({ ...o, key: i }))} rowKey="key"
                    columns={[{ title: '工单信息', key: 'order', render: (_, r) => (<div><div>{r.orderName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.orderId}</Text></div>) }, { title: '所属产品', key: 'prod', render: (_, r) => (<div><div>{r.productName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>) }, { title: '设备数', dataIndex: 'deviceCount', width: 80 }, { title: '操作', width: 60, render: (_, r) => <Button type="link" size="small" danger onClick={() => setEditOrders((prev) => prev.filter((o) => o.orderId !== r.orderId))}>移除</Button> }]} />
                )}
              </>
            )}

            {/* 导入设备 */}
            {viewTarget.strategy === 'custom' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <Upload showUploadList={false} accept=".xlsx,.xls,.csv" beforeUpload={() => { setCustomUploading(true); setCustomUploadPercent(0); const intv = setInterval(() => { setCustomUploadPercent((p) => { if (p >= 100) { clearInterval(intv); setCustomUploading(false); setTimeout(() => { setGroups((prev) => prev.map((g) => g.key === viewTarget.key ? { ...g, deviceCount: g.deviceCount + 50, updateTime: now() } : g)); setViewTarget((prev) => ({ ...prev, deviceCount: prev.deviceCount + 50, updateTime: now() })); message.success('上传完成，设备已更新'); }, 300); return 100; } return p + 20; }); }, 200); return false; }}>
                    <Button icon={<InboxOutlined />} loading={customUploading}>添加设备</Button>
                  </Upload>
                  <Button type="link" size="small" onClick={() => message.info('下载模板（Mock）')}>下载模板</Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>单次最大支持 20,000 台设备</Text>
                {customUploading && (
                  <div style={{ width: '100%', maxWidth: 400 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                      <Text type="secondary">上传中...</Text><Text type="secondary">{customUploadPercent}%</Text>
                    </div>
                    <div style={{ height: 4, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${customUploadPercent}%`, background: '#1677ff', borderRadius: 2, transition: 'width 0.2s' }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 保存并更新设备（非导入设备） */}
            {viewTarget.strategy !== 'custom' && (
              <Divider style={{ margin: '12px 0' }} />
            )}
            {viewTarget.strategy !== 'custom' && (
              <div style={{ textAlign: 'right' }}>
                <Button type="primary" loading={viewSaving} disabled={(() => { if (viewTarget.strategy === 'product') { const orig = (viewTarget.products || []).map((p) => p.productId).sort().join(','); const edit = editProducts.map((p) => p.productId).sort().join(','); return orig === edit; } if (viewTarget.strategy === 'order') { const orig = (viewTarget.orders || []).map((o) => o.orderId).sort().join(','); const edit = editOrders.map((o) => o.orderId).sort().join(','); return orig === edit; } return true; })()} onClick={handleViewSave}>保存并更新设备</Button>
              </div>
            )}
          </Card>
        )}

        {/* 筛选 */}
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>License</Text>
              <Input placeholder="请输入 License" value={viewLicense} onChange={(e) => setViewLicense(e.target.value)} allowClear style={{ width: 200 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>产品</Text>
              <Input.Group compact style={{ display: 'flex' }}>
                <Select value={viewProdType} onChange={(v) => { setViewProdType(v); setViewProdValue(''); }} style={{ width: 100 }}>
                  <Select.Option value="name">产品名称</Select.Option>
                  <Select.Option value="id">产品ID</Select.Option>
                </Select>
                {viewProdType === 'name' ? (
                  <Select value={viewProdValue} onChange={setViewProdValue} placeholder="请选择产品" allowClear showSearch optionFilterProp="label" style={{ width: 180 }} options={[...new Set(MOCK_LICENSES.map((r) => r.productName))].map((n) => ({ value: n, label: n }))} />
                ) : (
                  <Input value={viewProdValue} onChange={(e) => setViewProdValue(e.target.value)} placeholder="产品ID" allowClear style={{ width: 180 }} />
                )}
              </Input.Group>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>工单</Text>
              <Input.Group compact style={{ display: 'flex' }}>
                <Select value={viewOrderType} onChange={(v) => { setViewOrderType(v); setViewOrderValue(''); }} style={{ width: 100 }}>
                  <Select.Option value="name">工单名称</Select.Option>
                  <Select.Option value="id">工单ID</Select.Option>
                </Select>
                {viewOrderType === 'name' ? (
                  <Select value={viewOrderValue} onChange={setViewOrderValue} placeholder="请选择工单" allowClear showSearch optionFilterProp="label" style={{ width: 180 }} options={[...new Set(MOCK_LICENSES.map((r) => r.orderName).filter(Boolean))].map((n) => ({ value: n, label: n }))} />
                ) : (
                  <Input value={viewOrderValue} onChange={(e) => setViewOrderValue(e.target.value)} placeholder="工单ID" allowClear style={{ width: 180 }} />
                )}
              </Input.Group>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingTop: 18 }}>
              <Button type="primary" onClick={() => {}}>查询</Button>
              <Button onClick={() => { setViewLicense(''); setViewProdType('name'); setViewProdValue(''); setViewOrderType('name'); setViewOrderValue(''); }}>重置</Button>
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', paddingTop: 18 }}>
              <Button icon={<ExportOutlined />} onClick={() => message.success('导出成功')}>导出</Button>
            </div>
          </div>
        </Card>

        {/* License 列表 */}
        <Table columns={[
          { title: 'License', dataIndex: 'license', width: 200, render: (v) => <Button type="link" size="small" style={{ padding: 0, fontFamily: 'monospace' }} onClick={() => window.open(`/device/info?license=${v}`, '_blank')}>{v}</Button> },
          { title: '产品信息', key: 'prod', width: 180, render: (_, r) => (<div><div>{r.productName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>) },
          { title: '所属工单', key: 'order', width: 180, render: (_, r) => (<div><div>{r.orderName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.orderId}</Text></div>) },
          { title: '激活状态', dataIndex: 'activated', width: 100, render: (v) => <Tag color={v ? 'green' : 'default'} style={{ border: 'none' }}>{v ? '已激活' : '未激活'}</Tag> },
          { title: '绑定状态', dataIndex: 'bound', width: 100, render: (v) => <Tag color={v ? 'blue' : '#d9d9d9'} style={{ border: 'none' }}>{v ? '已绑定' : '未绑定'}</Tag> },
          { title: '操作', key: 'act', width: 100, render: (_, r) => <Button type="link" size="small" onClick={() => window.open(`/device/info?license=${r.license}`, '_blank')}>设备详情</Button> },
        ]} dataSource={viewLicensesFiltered} rowKey="key" scroll={{ x: 860 }} pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条 License` }} />
      </Drawer>

      {/* ── 添加产品/工单弹窗 ── */}
      <Modal title={viewVisible ? (viewTarget?.strategy === 'product' ? '添加产品' : '添加工单') : '添加工单'} open={addOrderModalVisible} onCancel={() => setAddOrderModalVisible(false)}
        onOk={() => { if (viewVisible) { if (viewTarget.strategy === 'product') addProductsToEdit(); else addOrdersToEdit(); } else { /* 创建中的添加工单逻辑，已内联 */ addOrdersToEdit(); } }}
        okText="确认添加" cancelText="取消" destroyOnClose
      >
        {viewVisible && viewTarget?.strategy === 'product' ? (
          <Form.Item label="选择产品（可多选）" style={{ marginTop: 16 }}>
            <Select mode="multiple" value={addOrderIds} onChange={setAddOrderIds} style={{ width: '100%' }} placeholder="请选择产品"
              options={PRODUCT_MOCK.map((p) => ({ value: p.value, label: p.label, disabled: editProducts.find((x) => x.productId === p.value) || (usedProductIds.includes(p.value) && !editProducts.find((x) => x.productId === p.value)) }))} />
          </Form.Item>
        ) : (
          <>
            <div style={{ marginBottom: 12, marginTop: 16 }}><Text strong>选择产品：</Text></div>
            <Select value={addOrderProduct} onChange={(v) => { setAddOrderProduct(v); setAddOrderIds([]); }} style={{ width: '100%', marginBottom: 16 }} placeholder="请选择产品" options={PRODUCT_MOCK.map((p) => ({ value: p.value, label: p.label }))} />
            <div style={{ marginBottom: 12 }}><Text strong>选择工单（可多选）：</Text></div>
            <Select mode="multiple" value={addOrderIds} onChange={setAddOrderIds} style={{ width: '100%' }} placeholder={addOrderProduct ? '请选择工单' : '请先选择产品'} disabled={!addOrderProduct}
              options={(ORDER_MOCK[addOrderProduct] || []).map((o) => ({ value: o.orderId, label: `${o.orderName}（${o.deviceCount}台）`, disabled: editOrders.find((x) => x.orderId === o.orderId) }))} />
          </>
        )}
      </Modal>

      {/* ── 名称编辑 ── */}
      <Modal title="编辑分组" open={nameModalVisible} onCancel={() => setNameModalVisible(false)} onOk={handleNameSave} okText="保存" cancelText="取消" destroyOnClose>
        <Form form={nameForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="分组名称" rules={[{ required: true, message: '请输入' }]}><Input placeholder="请输入分组名称" maxLength={30} /></Form.Item>
          <Form.Item name="description" label="分组描述"><Input.TextArea placeholder="请输入（可选）" maxLength={200} rows={3} showCount /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceGroups;
